import { ArrowLeft, Shield, Trash2, UserX, Scale } from "lucide-react";
import { BottomNav } from "./bottom-nav";
import { accountDeletionPublicCopy } from "@/app/copy/account-deletion-public";

interface AccountDeletionPublicPageProps {
  onBack: () => void;
  onOpenPrivacyPolicy?: () => void;
  activeTab: "home" | "favorites" | "wallet";
  onTabChange: (tab: string) => void;
  favoritesCount: number;
  onMenuClick: () => void;
  onPostAdClick: () => void;
  postAdBlockedByUnverifiedEmail?: boolean;
}

export function AccountDeletionPublicPage({
  onBack,
  onOpenPrivacyPolicy,
  activeTab,
  onTabChange,
  favoritesCount,
  onMenuClick,
  onPostAdClick,
  postAdBlockedByUnverifiedEmail = false,
}: AccountDeletionPublicPageProps) {
  const c = accountDeletionPublicCopy;

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 px-4 py-4">
            <button
              type="button"
              onClick={onBack}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors -ml-2"
              aria-label="Voltar"
            >
              <ArrowLeft className="w-6 h-6 text-white" />
            </button>
            <h1 className="text-lg font-medium text-white leading-tight">{c.pageTitle}</h1>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        <div className="bg-secondary/30 border border-primary/20 rounded-xl p-4">
          <div className="flex gap-3">
            <Shield className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{c.intro}</p>
              <p className="text-xs text-muted-foreground mt-2">{c.developerLine}</p>
            </div>
          </div>
        </div>

        <section aria-labelledby="steps-heading">
          <h2 id="steps-heading" className="text-base font-semibold text-foreground mb-3">
            {c.stepsTitle}
          </h2>
          <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
            {c.steps.map((s) => (
              <li key={s} className="leading-relaxed pl-1">
                {s.split("**").map((chunk, i) =>
                  i % 2 === 1 ? (
                    <strong key={i} className="text-foreground">
                      {chunk}
                    </strong>
                  ) : (
                    <span key={i}>{chunk}</span>
                  )
                )}
              </li>
            ))}
          </ol>
        </section>

        <section aria-labelledby="blocks-heading">
          <h2 id="blocks-heading" className="text-base font-semibold text-foreground mb-3">
            {c.blocksTitle}
          </h2>

          <div className="rounded-xl border border-border p-4 mb-4">
            <div className="flex gap-2 items-start mb-2">
              <Trash2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <h3 className="font-medium text-foreground">{c.deletedTitle}</h3>
            </div>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1.5 ml-1">
              {c.deletedBullets.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          </div>

          <div className="rounded-xl border border-border p-4 mb-4">
            <div className="flex gap-2 items-start mb-2">
              <UserX className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <h3 className="font-medium text-foreground">{c.anonymizedTitle}</h3>
            </div>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1.5 ml-1">
              {c.anonymizedBullets.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          </div>

          <div className="rounded-xl border border-border p-4">
            <div className="flex gap-2 items-start mb-2">
              <Scale className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <h3 className="font-medium text-foreground">{c.retainedTitle}</h3>
            </div>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1.5 ml-1">
              {c.retainedBullets.map((b) => (
                <li key={b}>{b}</li>
              ))}
              <li>
                <a
                  href={c.stripePrivacyURL}
                  className="text-primary underline underline-offset-2"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Política de privacidade da Stripe
                </a>
              </li>
            </ul>
          </div>
        </section>

        <section className="rounded-xl border border-dashed border-border p-4">
          <h2 className="text-base font-semibold text-foreground mb-2">{c.supportTitle}</h2>
          <p className="text-sm text-muted-foreground mb-3">{c.supportBody}</p>
          {onOpenPrivacyPolicy ? (
            <button
              type="button"
              onClick={onOpenPrivacyPolicy}
              className="text-sm font-medium text-primary underline underline-offset-2"
            >
              {c.privacyPolicyCta}
            </button>
          ) : null}
        </section>
      </div>

      <BottomNav
        activeTab={activeTab}
        onTabChange={onTabChange}
        favoritesCount={favoritesCount}
        onMenuClick={onMenuClick}
        onPostAdClick={onPostAdClick}
        postAdBlockedByUnverifiedEmail={postAdBlockedByUnverifiedEmail}
      />
    </div>
  );
}
